/* eslint-disable @typescript-eslint/no-explicit-any */
import { v4 as uuidv4 } from "uuid";

export type BridgeEnvelope = {
  source: "web";
  type: string;
  requestId?: string;
  payload?: any;
};

export type NativeResponse = {
  source?: "native";
  type: string;
  requestId?: string;
  status?: "ok" | "error";
  data?: any;
  error?: { code?: string; message?: string };
};

export type RNEventMessage = {
  event: string;
  params?: any;
};

type Resolver = {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timeoutId: any;
};

const DEFAULT_TIMEOUT = 10000;

function safeStringify(obj: any) {
  try {
    return JSON.stringify(obj);
  } catch {
    return JSON.stringify({ type: "serialization_error" });
  }
}

function tryParse<T = any>(data: any): T | null {
  if (data == null) return null;
  if (typeof data === "object") return data as T;
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as T;
    } catch {
      return null;
    }
  }
  return null;
}

class NativeBridge {
  private initialized = false;
  private resolvers = new Map<string, Resolver>();
  private eventWaiters = new Map<string, Set<Resolver>>();

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // iOS: message dispatched on window
    window.addEventListener("message", this.onMessage, false);
    // Android (react-native-webview): message dispatched on document
    try {
      (document as any)?.addEventListener?.("message", this.onMessage as any, false);
    } catch {}

    // 네이티브가 직접 호출할 수 있게 글로벌 훅 제공 (선택적)
    window.WebBiometricBridge = window.WebBiometricBridge || {};
    window.WebBiometricBridge.onNativeMessage = (message: any) => {
      this.onMessage({ data: message } as MessageEvent);
    };
  }

  private resolveEventWaiters(eventName: string, payload: any) {
    const waiters = this.eventWaiters.get(eventName);
    if (!waiters || waiters.size === 0) return;
    waiters.forEach((w) => {
      clearTimeout(w.timeoutId);
      w.resolve(payload);
    });
    this.eventWaiters.delete(eventName);
  }

  private onMessage = (event: MessageEvent<any>) => {
    const raw = event.data;

    // RN 이벤트(event/params) 규격 우선 처리
    const ev = tryParse<RNEventMessage>(raw) ?? (raw as RNEventMessage);
    if (ev && typeof ev === "object" && (ev as any).event) {
      this.resolveEventWaiters(ev.event, ev.params);
      return;
    }

    // 레거시(envelope type/requestId) 메시지 처리
    const data = tryParse<NativeResponse>(raw) ?? (raw as NativeResponse);
    if (!data || !data.type) return;

    if (data.requestId && this.resolvers.has(data.requestId)) {
      const entry = this.resolvers.get(data.requestId)!;
      clearTimeout(entry.timeoutId);

      if (data.status === "ok") {
        entry.resolve(data.data);
      } else {
        entry.reject(data.error ?? { message: "Unknown error" });
      }
      this.resolvers.delete(data.requestId);
      return;
    }

    // 필요 시 단방향 이벤트 브로드캐스트 가능
    // window.dispatchEvent(new CustomEvent(data.type, { detail: data }));
  };

  // 공통 전송: RN WebView, iOS messageHandlers, Android 인터페이스, postMessage 폴백
  private sendAny(message: any) {
    const messageString = safeStringify(message);

    // React Native WebView: string 필요
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(messageString);
      return;
    }

    // iOS WKWebView (messageHandlers): object 전달
    const mh = window.webkit?.messageHandlers;
    if (mh) {
      if ((mh as any).biometric?.postMessage) {
        (mh as any).biometric.postMessage(message);
        return;
      }
      if ((mh as any).native?.postMessage) {
        (mh as any).native.postMessage(message);
        return;
      }
      const firstKey = Object.keys(mh)[0];
      if (firstKey) {
        const handler = (mh as Record<string, any>)[firstKey];
        if (handler && typeof handler.postMessage === "function") {
          handler.postMessage(message);
          return;
        }
      }
    }

    // 안드로이드 커스텀 인터페이스: string 전달
    if (window.AndroidInterface?.postMessage) {
      window.AndroidInterface.postMessage(messageString);
      return;
    }

    // 개발/브라우저 환경 폴백
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, "*");
      return;
    }
    window.postMessage(message, "*");
  }

  private send(envelope: BridgeEnvelope) {
    this.sendAny(envelope);
  }

  request<T = any>(type: string, payload?: any, timeoutMs: number = DEFAULT_TIMEOUT): Promise<T> {
    this.init();
    const requestId = uuidv4();

    const envelope: BridgeEnvelope = {
      source: "web",
      type,
      requestId,
      payload,
    };

    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.resolvers.delete(requestId);
        reject({ code: "timeout", message: `Native response timeout for ${type}` });
      }, timeoutMs);

      this.resolvers.set(requestId, { resolve, reject, timeoutId });
      this.send(envelope);
    });
  }

  // RN 이벤트 규격: { event, params } 전송 후 기대 이벤트로 1회 응답 대기
  requestEvent<T = any>(
    eventName: string,
    params?: any,
    expectEvent?: string,
    timeoutMs: number = DEFAULT_TIMEOUT
  ): Promise<T> {
    this.init();
    const expected = expectEvent ?? this.defaultExpectEvent(eventName);

    return new Promise<T>((resolve, reject) => {
      const resolver: Resolver = {
        resolve,
        reject,
        timeoutId: setTimeout(() => {
          // 타임아웃 시 대기열에서 제거
          const set = this.eventWaiters.get(expected);
          if (set) {
            set.delete(resolver);
            if (set.size === 0) this.eventWaiters.delete(expected);
          }
          reject({ code: "timeout", message: `Timeout waiting for event "${expected}"` });
        }, timeoutMs),
      };

      const set = this.eventWaiters.get(expected) ?? new Set<Resolver>();
      set.add(resolver);
      this.eventWaiters.set(expected, set);

      this.sendAny({ event: eventName, params });
    });
  }

  private defaultExpectEvent(eventName: string): string {
    if (eventName === "get_biometric_status") return "biometric_status";
    if (eventName === "request_biometric") return "biometric_result";
    // 기본 규칙: foo -> foo_result
    return `${eventName}_result`;
    }

  // 생체인식 전용 헬퍼
  async getBiometricStatus(timeoutMs: number = 5000): Promise<{
    available: boolean;
    enrolled: boolean;
    types: number[];
    error?: string;
  }> {
    return this.requestEvent("get_biometric_status", undefined, "biometric_status", timeoutMs);
  }

  async requestBiometric(
    options?: { promptMessage?: string; cancelLabel?: string; disableDeviceFallback?: boolean },
    timeoutMs: number = 15000
  ): Promise<{ success: boolean; error?: string }> {
    return this.requestEvent("request_biometric", options ?? {}, "biometric_result", timeoutMs);
  }
}

export const nativeBridge = new NativeBridge();
